import axios from 'axios'
import { toast } from 'react-toastify'
import _ from 'lodash'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import classes from './FamiliesPage.module.scss'
import MembersSearchInput from './components/MembersSearchInput'
import FilterButton from './components/FilterButton'
import { deserialize } from '../../utility/serialization'
import LoadingSpinner from '../common/loader/legacy-loaders/LoadingSpinner'
import { requestFamilies } from '../../actions/familyActions'
import FamiliesTable from './components/FamiliesTable'

const PER_PAGE = 10
const DEBOUNCE_DURATION = 400
const FILTER_OPTIONS = {
  ALL: 'ALL',
  ACTIVE: 'ACTIVE',
}

const FamiliesPage = () => {
  const abortController = useRef(null)
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const [families, setFamilies] = useState(null)
  const [pagination, setPagination] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [filterOption, setFilterOption] = useState(FILTER_OPTIONS.ALL)
  const [isLoading, setIsLoading] = useState(false)

  const fetchFamilies = useCallback(
    ({ page, search, active, append }) =>
      requestFamilies(communityId, page, PER_PAGE, search, active, abortController.current?.signal)
        .then(response => {
          const data = deserialize(response.data)
          setFamilies(prev => {
            if (append) { return [...prev, ...data.families] }
            return data.families
          })
          setPagination(data.meta.pagination)
        }),
    [communityId]
  )

  const updateFamilies = useCallback(
    (params) => {
      setIsLoading(true)
      fetchFamilies({
        page: 1,
        search: searchText,
        active: filterOption === FILTER_OPTIONS.ACTIVE,
        append: false,
        ...params,
      })
        .catch((res) => {
          if (!axios.isCancel(res)) {
            toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT })
          }
        })
        .finally(() => setIsLoading(false))
    },
    [fetchFamilies, filterOption, searchText]
  )

  const fetchMoreFamilies = () => {
    updateFamilies({ page: pagination?.page + 1, append: true })
  }

  const debouncedSearch = useMemo(
    () => _.debounce((search) => updateFamilies({ search }), DEBOUNCE_DURATION),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleSearch = (e) => {
    setSearchText(e.target.value)
    debouncedSearch(e.target.value)
  }

  const handleFilter = (option) => {
    setFilterOption(option)
    updateFamilies({ active: option === FILTER_OPTIONS.ACTIVE })
  }

  useEffect(() => {
    abortController.current = new AbortController()

    updateFamilies({ search: '', active: false })

    return () => abortController.current.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <main className={classes.wrapper}>
      <header className='py-4 d-flex align-items-center'>
        <div className={classes.searchWrapper}>
          <h4 className={classNames('font-weight-bold mb-0', classes.title)}>
            Families {!!pagination && <>({pagination.totalCount})</>}
          </h4>
          <MembersSearchInput placeholder='Search for Families' value={searchText} onChange={handleSearch} />
          {isLoading && <LoadingSpinner classNames={{ wrapper: classes.loading }} />}
        </div>
        <div className={classes.filterOptions}>
          <FilterButton
            text='All'
            onClick={() => handleFilter(FILTER_OPTIONS.ALL)}
            isActive={filterOption === FILTER_OPTIONS.ALL}
          />
          <FilterButton
            text='Active'
            onClick={() => handleFilter(FILTER_OPTIONS.ACTIVE)}
            isActive={filterOption === FILTER_OPTIONS.ACTIVE}
          />
        </div>
      </header>
      <FamiliesTable families={families} fetchData={fetchMoreFamilies} pagination={pagination} />
    </main>
  )
}

export default FamiliesPage
